import {
  Input,
  InputAdornmentButton,
  ArticleParagraph2,
  Tags,
  type ITagOption,
  type TTagOptionValue,
} from '@cian/ui-kit';
import { clsx } from 'clsx';
import { FC, useRef, useState, useCallback, FormEvent, useMemo } from 'react';

import styles from './TagsInput.css';

interface ITagsInputProps {
  id: string;
  placeholder?: string;
  maxLength?: number;
  value: [string, ...string[]] | null;
  onAdd(value: TTagOptionValue): void;
  onRemove(value: TTagOptionValue): void;
}

export const TagsInput: FC<ITagsInputProps> = ({ id, placeholder, maxLength = 30, value: values, onAdd, onRemove }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState<string>('');

  const handleChange = useCallback(
    (event: FormEvent<HTMLInputElement>) => {
      const {
        currentTarget: { value },
      } = event;

      setValue(value);
    },
    [setValue],
  );

  const handleAdd = useCallback(() => {
    const trimmedValue = value.trim();

    if (!trimmedValue || values?.some(v => v === trimmedValue)) {
      return;
    }

    onAdd(trimmedValue);
    setValue('');

    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [values, value, onAdd, setValue]);

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        handleAdd();
      }
    },
    [handleAdd],
  );

  const handleRemove = useCallback(
    (removedOption: ITagOption) => {
      onRemove(removedOption.value);
    },
    [onRemove],
  );

  const tagOptions = useMemo(() => (values ? values.map(value => ({ value, label: value })) : []), [values]);

  return (
    <div>
      <div id={`${id}_input`} className={clsx(!!values && styles['input-container--with-tags'])}>
        <Input
          ref={inputRef}
          width={260}
          rightAdornment={
            <InputAdornmentButton
              title="Добавить тег"
              aria-controls={`${id}_input`}
              aria-keyshortcuts="Enter"
              aria-label="Добавить тег"
              onClick={handleAdd}
            >
              <ArticleParagraph2 color="current_color">Добавить</ArticleParagraph2>
            </InputAdornmentButton>
          }
          placeholder={placeholder}
          maxLength={maxLength}
          value={value}
          onKeyPress={handleKeyPress}
          onChange={handleChange}
        />
      </div>
      {values && <Tags options={tagOptions} onClose={handleRemove} />}
    </div>
  );
};
